import React from 'react';
import MuiSelect, {
  SelectProps as MuiSelectProps,
} from '@material-ui/core/Select';
import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import InputLabel, { InputLabelProps } from '@material-ui/core/InputLabel';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';

type SelectProps = {
  selectProps?: MuiSelectProps,
  formControlProps?: FormControlProps,
  inputLabelProps?: InputLabelProps,
  menuItemsProps?: Array<MenuItemProps>,
  typographyProps?: TypographyProps,
  readOnly?: boolean,
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    verticalAlign: 'baseline',
    padding: 2,
  },
  select: {
    whiteSpace: 'normal',
    backgroundColor: theme.palette.grey[200],
    padding: '6px 32px 6px 10px',
    '&:focus': {
      backgroundColor: theme.palette.grey[200],
    },
  },
}));

const Select = ({
  selectProps,
  formControlProps,
  inputLabelProps,
  menuItemsProps,
  typographyProps,
  readOnly,
}: SelectProps) => {
  const classes = useStyles();

  const labelId = React.useRef(
    inputLabelProps?.id ||
      selectProps?.labelId ||
      'select-' + (new Date().getTime() + Math.random()).toString(),
  );

  const [value, setValue] = React.useState(selectProps?.value);

  if (readOnly) {
    const item = menuItemsProps?.find?.(
      ({ value: itemValue }) => itemValue === value,
    );
    return (
      <Typography
        variant="inherit"
        component="span"
        {...typographyProps}
        className={clsx('text-break-all', typographyProps?.className)}
      >
        {item?.children}
      </Typography>
    );
  }

  const handleChange = (event) => setValue(event.target.value);

  return (
    <FormControl
      variant="outlined"
      size="small"
      {...formControlProps}
      className={clsx(classes.formControl, formControlProps?.className)}
    >
      <InputLabel id={labelId.current}>{selectProps?.label}</InputLabel>
      <MuiSelect
        {...selectProps}
        classes={{
          ...selectProps?.classes,
          root: clsx(classes.select, selectProps?.classes?.root),
        }}
        labelId={labelId.current}
        value={value}
        onChange={(event) => {
          handleChange(event);
          selectProps?.onChange?.(event);
        }}
      >
        {menuItemsProps?.map?.(({ value, style, ...otherItemProps }, index) => (
          <MenuItem
            key={index}
            value={value}
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
              ...style,
            }}
            {...otherItemProps}
          />
        ))}
      </MuiSelect>
    </FormControl>
  );
};

export default Select;
